'use client';

import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEffect, useMemo } from 'react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TooltipProvider } from '@/components/ui/tooltip/tooltip';
import { Separator } from '@/components/ui/separator/separator';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { getTenantConfig } from '@/tenants/config';
import { ToolbarButton } from './toolbar-button';
import { LinkPopover } from './link-popover';

interface BlogEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

function BlogToolbar({ editor, isLight }: { editor: Editor | null; isLight: boolean }) {
  if (!editor) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-0.5 p-1 rounded-t-md border-b',
          isLight ? 'border-stone-200 bg-stone-50' : 'border-stone-700 bg-stone-800/50',
        )}
      >
        {/* Text formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          tooltip="Bold"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          tooltip="Italic"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          tooltip="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>

        <Separator
          orientation="vertical"
          className={cn('mx-1 h-6', isLight ? 'bg-stone-300' : 'bg-stone-700')}
        />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          tooltip="Heading"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          tooltip="Subheading"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>

        <Separator
          orientation="vertical"
          className={cn('mx-1 h-6', isLight ? 'bg-stone-300' : 'bg-stone-700')}
        />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          tooltip="Bullet list"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          tooltip="Numbered list"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <Separator
          orientation="vertical"
          className={cn('mx-1 h-6', isLight ? 'bg-stone-300' : 'bg-stone-700')}
        />

        {/* Link */}
        <LinkPopover editor={editor} />
      </div>
    </TooltipProvider>
  );
}

export function BlogEditor({
  content,
  onChange,
  placeholder = 'Write your fundraiser update...',
  className,
}: BlogEditorProps) {
  const tenantSlug = useTenantSlug();
  // Light-theme tenants (DGD, UWR) get the light editor; DGR stays dark.
  const isLight = getTenantConfig(tenantSlug).donationTheme === 'light';

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          // --primary resolves to each light tenant's brand color (dgd gold, uwr orange).
          class: isLight ? 'text-primary underline' : 'text-dgr-gold underline',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    [placeholder, isLight],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none',
          isLight ? '' : 'prose-invert',
          'min-h-[150px] p-4 focus:outline-none',
          'prose-headings:font-semibold prose-headings:tracking-tight',
          isLight ? 'prose-headings:text-stone-900' : 'prose-headings:text-stone-100',
          'prose-h2:text-xl prose-h3:text-lg',
          'prose-p:leading-relaxed',
          isLight ? 'prose-p:text-stone-800' : 'prose-p:text-stone-300',
          isLight
            ? 'prose-a:text-dgd-gold prose-a:underline'
            : 'prose-a:text-dgr-gold prose-a:underline',
          isLight ? 'prose-strong:text-stone-900' : 'prose-strong:text-stone-100',
          isLight
            ? 'prose-ul:text-stone-800 prose-ol:text-stone-800'
            : 'prose-ul:text-stone-300 prose-ol:text-stone-300',
          isLight ? 'prose-li:marker:text-stone-400' : 'prose-li:marker:text-stone-500',
        ),
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // Sync content when prop changes externally (e.g. form reset)
  useEffect(() => {
    if (!editor) return;
    if (content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return (
    <div
      className={cn(
        'rounded-md overflow-hidden border',
        isLight ? 'border-stone-300 bg-white' : 'border-stone-700 bg-stone-900',
        className,
      )}
    >
      <BlogToolbar editor={editor} isLight={isLight} />
      <EditorContent editor={editor} />
    </div>
  );
}
